<template>
  <v-dialog
    v-model="showSettingsModal"
    max-width="300px"
  >
    <v-card>
      <v-card-title>
        <v-col class="col-grow pa-0">
          <span class="darkblue--text subtitle-2 text-uppercase">
            Settings
          </span>
        </v-col>

        <v-spacer />

        <v-col class="col-auto pa-0">
          <v-btn
            :ripple="false"
            icon
            small
            @click.stop="onClose()"
          >
            <v-icon
              color="brightblue"
            >
              close
            </v-icon>
          </v-btn>
        </v-col>
      </v-card-title>

      <v-divider />

      <v-card-text
        class="mt-6"
      >
        <v-row
          class="align-center my-3"
        >
          <v-col>
            <v-row>
              <v-col class="mx-3">
                <v-row class="font-weight-medium">
                  <span>Dark Mode</span>
                </v-row>

                <v-row class="caption">
                  Tailor the app's appearance for you and your environment.
                </v-row>

                <v-row>
                  <v-col class="px-0">
                    <v-switch
                      v-model="currentDarkMode"
                      :label="currentDarkMode ? 'On' : 'Off'"
                      inset
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-divider />
          </v-col>
        </v-row>

        <alias-mode v-if="showAliasSettings" />

        <dev-mode v-if="showDevSettings" />

        <demo-mode />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import AliasMode from 'src/auth/components/AliasMode'
import DemoMode from 'src/auth/components/DemoMode'
import DevMode from 'src/auth/components/DevMode'

const { mapGetters: mapAuthGetters, mapState: mapAuthState } = createNamespacedHelpers('auth')
const { mapState: mapLayoutState } = createNamespacedHelpers('layout')

export default {
  name: 'SettingsModal',
  components: { AliasMode, DemoMode, DevMode },
  data() {
    return {
      showThemeSettings: false,
    }
  },
  computed: {
    ...mapAuthGetters([
      'hasDevPermission',
      'inAliasMode',
      'isAliasWriter',
      'inDarkMode',
      'inDemoMode',
      'isDemoWriter',
      'isInternalAdminUser',
      'permissions',
      'selfEmail',
      'settings',
    ]),
    ...mapAuthState({
      demoMode: (state) => state.demoMode,
      didDemoModeFail: (state) => state.didDemoModeFail,
      isLoadingDemoMode: (state) => state.isLoadingDemoMode,
      user: (state) => state.user,

      confirmDemoModeChange: (state) => state.confirmDemoModeChange,
      errorDemoModeChange: (state) => state.errorDemoModeChange,
      inputDemoMode: (state) => state.inputDemoMode,
      inputDemoModeChange: (state) => state.inputDemoModeChange,
    }),
    ...mapLayoutState({
      isModalVisibleSettings: (state) => state.isModalVisibleSettings,
      themes: (state) => state.themes,
    }),
    showAliasSettings() {
      return this.isAliasWriter || this.inAliasMode
    },
    showDevSettings() {
      return this.hasDevPermission
    },
    showSettingsModal: {
      get() {
        return this.isModalVisibleSettings
      },
      set(value) {
        this.$store.commit('layout/isModalVisibleSettings', value, { root: true })
      },
    },
    currentDarkMode: {
      get() {
        return this.inDarkMode
      },
      set(newValue) {
        this.$store.dispatch('layout/setDarkMode', newValue, { root: true })
        this.$vuetify.theme.dark = newValue || false
      },
    },

    userConfirmDemoModeChange: {
      get() {
        return this.confirmDemoModeChange
      },
      set(value) {
        this.$store.commit('auth/confirmDemoModeChange', value)
      },
    },
    userErrorDemoModeChange: {
      get() {
        return this.errorDemoModeChange
      },
      set(value) {
        this.$store.commit('auth/errorDemoModeChange', value)
      },
    },
    userInputDemoMode: {
      get() {
        return this.inputDemoMode
      },
      set(value) {
        this.$store.commit('auth/inputDemoMode', value)
      },
    },
    userInputDemoModeChange: {
      get() {
        return this.inputDemoModeChange
      },
      set(value) {
        this.$store.commit('auth/inputDemoModeChange', value)
      },
    },
    currentDemoMode: {
      get() {
        return this.demoMode
      },
      set(newValue) {
        this.userInputDemoMode = newValue || false
        this.userConfirmDemoModeChange = true

        return this.userInputDemoMode
      },
    },
    demoModeErrorMessages() {
      return this.userErrorDemoModeChange ? ['Check for typos.'] : null
    },
    inputDemoModeLabel() {
      const { userInputDemoMode, pendingDemoModeChange } = this
      const action = userInputDemoMode ? 'On' : 'Off'

      if (pendingDemoModeChange) return `Turning ${action}`

      return action
    },
    inputTest() {
      const action = this.userInputDemoMode ? 'on' : 'off'
      return `demo ${action}`
    },
    pendingDemoModeChange() {
      const { userConfirmDemoModeChange, userInputDemoMode, isLoadingDemoMode } = this
      const invalidState = typeof userInputDemoMode !== 'boolean'

      if (invalidState) return false

      return userConfirmDemoModeChange || isLoadingDemoMode
    },
  },
  watch: {
    async showSettingsModal(newValue) {
      if (newValue && this.isAliasWriter && (!this.externals || !this.externals.length)) {
        const externals = await this.$store.dispatch('auth/getExternalUsers', null, { root: true })
        const aliasOptions = externals.map((external) => ({
          text: external.docId,
          value: external.docId,
        }))

        this.$store.commit('auth/aliasOptions', aliasOptions, { root: true })
      }
    },
  },
  created() {
    this.userInputDemoMode = this.demoMode
  },
  methods: {
    onClose() {
      this.showSettingsModal = false
      this.cancelDemoModeChange()
    },
    onShow() {
      this.showSettingsModal = true
    },

    resetErrorDemoModeChange() {
      this.userErrorDemoModeChange = false
    },
    cancelDemoModeChange() {
      this.userInputDemoModeChange = null
      this.userErrorDemoModeChange = false
      this.userConfirmDemoModeChange = false

      return false
    },
  },
}
</script>
